import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from 'react-router-dom';
import { LandingPage } from '~/routes/landing';

/**
 * Declarative-режим react-router v7.
 * Добавить страницу: создай компонент в src/routes/foo.tsx, добавь объект сюда.
 *
 * Админка (admin-panel) — только под /admin и только через `lazy`: код её страниц уходит в отдельные чанки
 * и не грузится на лендинге. Не импортируй src/routes/admin-* статически ни здесь, ни где-либо ещё.
 */
const adminLoading = <div className="min-h-screen bg-slate-50 grid place-items-center text-sm text-slate-500">Загрузка…</div>;

/** Положение об акции (promo-rules) — отдельный чанк: текст ~46 тыс. знаков лендингу не нужен. */
const rulesLoading = <div className="min-h-screen bg-[#FBF4E6]" />;

/**
 * Страницы участника. Положение открывается в том же окне (promo-rules): ScrollRestoration открывает его
 * с начала, а «Назад к акции» возвращает лендинг на место, откуда ушли (ссылка в footer — к footer).
 */
function ParticipantPages() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    element: <ParticipantPages />,
    children: [
      { path: '/', element: <LandingPage /> },
      {
        path: '/rules',
        hydrateFallbackElement: rulesLoading,
        lazy: async () => ({ Component: (await import('~/routes/rules')).RulesPage }),
      },
    ],
  },
  {
    path: '/admin/login',
    hydrateFallbackElement: adminLoading,
    lazy: async () => ({ Component: (await import('~/routes/admin-login')).AdminLoginPage }),
  },
  {
    path: '/admin',
    hydrateFallbackElement: adminLoading,
    lazy: async () => ({ Component: (await import('~/routes/admin-layout')).AdminLayout }),
    children: [
      { index: true, element: <Navigate to="/admin/tickets" replace /> },
      {
        path: 'tickets',
        lazy: async () => ({ Component: (await import('~/routes/admin-tickets-layout')).AdminTicketsLayout }),
        children: [
          { index: true, lazy: async () => ({ Component: (await import('~/routes/admin-tickets')).AdminTicketsPage }) },
          { path: 'search', lazy: async () => ({ Component: (await import('~/routes/admin-tickets-search')).AdminTicketsSearchPage }) },
        ],
      },
      {
        path: 'draw',
        lazy: async () => ({ Component: (await import('~/routes/admin-draw-layout')).AdminDrawLayout }),
        children: [
          { index: true, lazy: async () => ({ Component: (await import('~/routes/admin-draw-plan')).AdminDrawPlanPage }) },
          { path: 'items', lazy: async () => ({ Component: (await import('~/routes/admin-draw-items')).AdminDrawItemsPage }) },
          { path: 'messages', lazy: async () => ({ Component: (await import('~/routes/admin-draw-messages')).AdminDrawMessagesPage }) },
          { path: 'awards', lazy: async () => ({ Component: (await import('~/routes/admin-draw-awards')).AdminDrawAwardsPage }) },
          { path: 'blocked', lazy: async () => ({ Component: (await import('~/routes/admin-draw-blocked')).AdminDrawBlockedPage }) },
          { path: 'settings', lazy: async () => ({ Component: (await import('~/routes/admin-draw-settings')).AdminDrawSettingsPage }) },
          { path: 'changes', lazy: async () => ({ Component: (await import('~/routes/admin-draw-changes')).AdminDrawChangesPage }) },
        ],
      },
      { path: 'participants', lazy: async () => ({ Component: (await import('~/routes/admin-participants')).AdminParticipantsPage }) },
      { path: 'participants/:participantId', lazy: async () => ({ Component: (await import('~/routes/admin-participant')).AdminParticipantPage }) },
      { path: 'nl-outbox', lazy: async () => ({ Component: (await import('~/routes/admin-nl-outbox')).AdminNlOutboxPage }) },
      { path: 'translations', lazy: async () => ({ Component: (await import('~/routes/admin-translations')).AdminTranslationsPage }) },
      { path: 'users', lazy: async () => ({ Component: (await import('~/routes/admin-users')).AdminUsersPage }) },
      // Неизвестный адрес под /admin — в раздел по умолчанию; без сессии layout всё равно уведёт на вход.
      { path: '*', element: <Navigate to="/admin/tickets" replace /> },
    ],
  },
]);
